import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import {
  selectActiveAppKey,
  selectActiveCompanyKey,
  selectAppState,
  type AppState,
} from './active-context-state';
import { bbkApiSlice, type AdminApiState } from './api';
import { authStatusFromQuery } from './auth-status';
import { type ActiveApp, type ActiveCompany, type ActiveUser } from './session';

type ActiveContextStoreState = { readonly app: AppState } & AdminApiState;

const selectAuthenticatedUser = (
  state: ActiveContextStoreState
): ActiveUser | undefined => {
  const query = bbkApiSlice.endpoints.getCurrentUser.select()(state);
  return authStatusFromQuery(query) === 'authenticated'
    ? query.data
    : undefined;
};

const selectActiveCompany = createSelector(
  [selectAuthenticatedUser, selectActiveCompanyKey],
  (user, companyKey): ActiveCompany | undefined =>
    user?.companies.find((company) => company.internal_name === companyKey)
);

const selectActiveApp = createSelector(
  [selectActiveCompany, selectActiveAppKey],
  (company, appKey): ActiveApp | undefined =>
    company?.applications.find((app) => app.encoded_id === appKey)
);

// Scoped consumers have historically treated these values as initialized.
// Keep that public type contract while unscoped or no-longer-valid selections
// continue to resolve to undefined at runtime.
export const selectUser = (state: ActiveContextStoreState) =>
  selectAuthenticatedUser(state)!;

export const selectCompany = (state: ActiveContextStoreState) =>
  selectActiveCompany(state)!;

export const selectApp = (state: ActiveContextStoreState) =>
  selectActiveApp(state)!;

export const selectCompanyName = (state: ActiveContextStoreState) =>
  selectCompany(state)?.name;

export const selectAppDisplayName = (state: ActiveContextStoreState) =>
  selectApp(state)?.display_name;

export const selectCompanyInternalName = (state: ActiveContextStoreState) =>
  selectCompany(state)?.internal_name ?? '_';

export const selectCompanyId = (
  state: ActiveContextStoreState
): number | string => selectCompany(state)?.id ?? '_';

export const selectAppId = (state: ActiveContextStoreState) =>
  selectApp(state)?.encoded_id ?? '_';

export { selectAppState };

export const useSelectCurrentUser = () => {
  const { data, status, error } =
    bbkApiSlice.endpoints.getCurrentUser.useQuery();

  // Gate on the shared auth status so a user retained after a 401 (RTK keeps the
  // last fulfilled data on a rejected refetch) is not read as a live session.
  return useMemo(
    () =>
      authStatusFromQuery({ status, data, error }) === 'authenticated'
        ? data
        : undefined,
    [data, status, error]
  );
};

export const useSelectCurrentCompany = () => useSelector(selectCompany);

export const useSelectCompanyApps = () => {
  const company = useSelectCurrentCompany();

  return useMemo(() => company?.applications ?? [], [company]);
};

export const useSelectCurrentApp = () => useSelector(selectApp);
